import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import { UserMetadataService } from '@core/services/user-metadata.service';
import { dvNumberMaskPresets } from '@devinco/ui/dds';
import {
  DvCheckboxElement,
  DvDatePickerLegacyElement,
  DvDropdownClientSideElement,
  DvDynamicFormElement,
  DvNumberboxElement,
  DvTextboxElement,
} from '@devinco/ui/legacy';
import { activeRecordFilter } from '@shared/constants/active-record-filter';
import { Country } from '@shared/constants/country';
import { Entity } from '@shared/constants/entity';
import { QuickFilterModel } from '@shared/constants/quick-filters/quick-filter-models';

@Injectable()
export class VehicleInformationTabService {
  private restApi = inject(RestApiService);
  private userMetadataService = inject(UserMetadataService);

  /** Vehicle elements */
  elementsVehicle: DvDynamicFormElement[] = [
    new DvNumberboxElement({
      colspan: 6,
      key: 'VehicleID',
      hidden: true,
    }),
    new DvTextboxElement({
      colspan: 6,
      key: 'RegistrationNumber',
      label: 'Vehicle.RegistrationNumber',
      validators: this.userMetadataService.hasSkiDomain()
        ? []
        : [Validators.required],
    }),
    new DvTextboxElement({
      key: 'CreatedTime',
      hidden: true,
    }),
    new DvDatePickerLegacyElement({
      colspan: 6,
      key: 'RegistrationDate',
      label: 'Vehicle.RegistrationDate',
    }),
    new DvTextboxElement({
      key: 'VehicleDescription',
      label: 'Vehicle.VehicleDescription',
      validators: [Validators.required],
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      closeOnSelect: false,
      key: 'DepartmentID',
      label: 'Vehicle.DepartmentDescription',
      labelKeys: ['DepartmentDescription'],
      valueKey: 'DepartmentID',
      isCheckboxEnabled: true,
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.Department,
          fields: ['DepartmentID', 'DepartmentDescription'],
          filterModel: activeRecordFilter,
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
    new DvNumberboxElement({
      colspan: 6,
      key: 'Mileage',
      label: 'Vehicle.Mileage',
    }),
    new DvTextboxElement({
      colspan: 12,
      key: 'Comment',
      label: 'Vehicle.Comment',
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'VehicleTypeID',
      label: 'Vehicle.VehicleType',
      labelKeys: ['VehicleTypeDescription'],
      valueKey: 'VehicleTypeID',
      validators: [Validators.required],
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.VehicleType,
          fields: ['VehicleTypeID', 'VehicleTypeDescription'],
        };
        this.restApi.setTranslatedDropdownData(
          request,
          params,
          'VehicleTypeDescription',
          'VehicleType'
        );
      },
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'FuelTypeID',
      label: 'Vehicle.FuelType',
      labelKeys: ['FuelTypeDescription'],
      valueKey: 'FuelTypeID',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.FuelType,
          fields: ['FuelTypeID', 'FuelTypeDescription'],
          filterModel: {
            CountryID: {
              filter:
                this.userMetadataService.getUserCountry() === Country.Norway
                  ? 1
                  : 2,
              filterType: 'number',
              type: 'equals',
            },
          },
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
    new DvNumberboxElement({
      colspan: 6,
      key: 'FuelConsumption',
      label: 'Vehicle.FuelConsumption',
      fieldMask: dvNumberMaskPresets.FormattedDecimalNumber,
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'EmissionClassID',
      label: 'Vehicle.EmissionClass',
      labelKeys: ['EmissionClassDescription'],
      valueKey: 'EmissionClassID',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.EmissionClass,
          fields: ['EmissionClassID', 'EmissionClassDescription'],
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
    new DvNumberboxElement({
      colspan: 12,
      key: 'CO2Emission',
      label: 'Vehicle.Co2Emission',
      fieldMask: dvNumberMaskPresets.FormattedNumber,
    }),
    new DvCheckboxElement({
      checkboxLabel: 'Vehicle.HeavyVehicle',
      key: 'HeavyVehicle',
      colspan: 6,
    }),
    new DvCheckboxElement({
      checkboxLabel: 'Vehicle.AllowShowInMap',
      key: 'AllowShowInMap',
      colspan: 6,
      readonly: true,
    }),
    new DvCheckboxElement({
      checkboxLabel: 'Vehicle.ExpenseLocationUseDiscount',
      key: 'ExpenseLocationUseDiscount',
      colspan: 6,
    }),
    new DvCheckboxElement({
      checkboxLabel: 'Vehicle.IsAssetLocation',
      key: 'IsAssetLocation',
      colspan: 6,
    }),
    new DvCheckboxElement({
      key: 'HasFind',
      colspan: 6,
      hidden: true,
    }),
    new DvCheckboxElement({
      key: 'HasTrip',
      hidden: true,
    }),
  ];

  /** ViaTracks unit elements */
  elementsVT: DvDynamicFormElement[] = [
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'TrackerID',
      label: 'Tracker.Unit', // ViaTracks Unit
      labelKeys: ['TrackerSerial'],
      valueKey: 'TrackerID',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.Tracker,
          fields: ['TrackerID', 'TrackerDescription', 'TrackerSerial'],
          filterModel: {
            IsInUse: {
              filterType: 'boolean',
              type: 'equals',
              filter: 0,
            },
            NeverSleep: {
              filterType: 'set',
              values: [0],
            },
          },
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'DefaultUserID',
      label: 'User.DefaultDriver',
      labelKeys: ['UserDescription', 'Username'],
      valueKey: 'UserID',
      labelKeysSeparator: ' - ',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.User,
          fields: ['UserID', 'UserDescription', 'Username'],
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'ExpenseSetupID',
      label: 'ExpenseSetup.SelectExpenseSetup',
      labelKeys: ['ExpenseSetupDescription'],
      valueKey: 'ExpenseSetupID',
      disabled: true,
      ready: params => {
        const request: GetRowsRequest = {
          entity: Entity.ExpenseSetupVehicle,
          fields: ['ExpenseSetupID', 'ExpenseSetupDescription'],
          filterModel: {
            CountryID: {
              filter: 1,
              filterType: 'number',
              type: 'equals',
            },
          },
        };
        this.restApi.setTranslatedDropdownData(
          request,
          params,
          'ExpenseSetupDescription',
          'Expense'
        );
      },
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'CardReaderID',
      labelKeys: ['CardReaderSerial'],
      label: 'CardReader.CardReaderSerial',
      valueKey: 'CardReaderID',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.AvailableCardReaders,
          fields: ['CardReaderID', 'CardReaderSerial'],
          filterModel: {
            CardReaderID: {
              filterType: 'number',
              type: 'notBlank',
            },
            Active: { filterType: 'set', values: ['Active'] },
            ...QuickFilterModel.Active,
          },
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      closeOnSelect: false,
      key: 'TagDescription',
      label: 'TagVehicle.SelectTags', // Select Tags from the list
      isMultipleSelectionEnabled: true,
      isCheckboxEnabled: true,
      valueKey: 'TagDescription',
      labelKeys: ['TagDescription'],
      ready: params => {
        const request: GetRowsRequest = {
          entity: 'TagVehicle',
          fields: ['TagDescription'],
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
    new DvCheckboxElement({
      key: 'ShowRecord',
      hidden: true,
    }),
  ];

  getSkiDomainElements(): DvDynamicFormElement[] {
    return [
      new DvCheckboxElement({
        key: 'IsRouteNetworkMaintainer',
        colspan: 12,
        checkboxLabel: 'Vehicle.IsRouteNetworkMaintainer',
      }),
      new DvDropdownClientSideElement({
        key: 'RouteNetworkMaintainerTypeID',
        extra: { ignoreInRequest: true },
        label: 'Vehicle.RouteNetworkMaintainerType',
        colspan: 6,
        labelKeys: ['TranslationKey'],
        valueKey: 'RouteNetworkMaintainerTypeID',
        ready: params => {
          const request: GetRowsRequest = {
            entity: Entity.RouteNetworkMaintainerType,
            fields: ['TranslationKey', 'RouteNetworkMaintainerTypeID'],
          };
          this.restApi.setTranslatedDropdownData(
            request,
            params,
            'TranslationKey'
          );
        },
      }),
      new DvNumberboxElement({
        key: 'RouteNetworkMaintainer_ParkedVisibilityDurationMinutes',
        colspan: 6,
        label: 'Vehicle.ParkedVisibilityDurationMinutes',
      }),

      new DvCheckboxElement({
        key: 'IsPublic',
        colspan: 6,
        checkboxLabel: 'Vehicle.IsPublic',
      }),
      new DvCheckboxElement({
        key: 'RouteNetworkMaintainer_HideIfNoExternalPower',
        colspan: 6,
        checkboxLabel: 'Vehicle.HideIfNoExternalPower',
      }),
      new DvNumberboxElement({
        key: 'RouteNetworkMaintainerTypeID',
        hidden: true,
      }),
    ];
  }
}
